import React, { useContext, useId, useMemo } from "react";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import Tooltip from "@mui/material/Tooltip";
import { CustomerDeliveryAddress } from "../../../../../data/entities";
import { ROLE } from "../../../../../data/enums";
import { AuthenticationContext } from "../../../../../provider/AuthenticationProvider";
import AddSectionIcon from "../../../../../shared/svg/AddSection";
import {
    getDeliveryAddressFieldErrors,
    getDefaultDeliveryAddressValidationMessage,
    MAX_CUSTOMER_DELIVERY_ADDRESSES,
    removeDeliveryAddress,
    selectDefaultDeliveryAddress,
} from "../../deliveryAddresses";
import {
    AddButton,
    AddButtonText,
    ActionSlot,
    AddressInput,
    AddressList,
    AddressGroup,
    AddressSection,
    Counter,
    DeleteButton,
    DefaultBadge,
    DefaultControl,
    DefaultRow,
    EmptyState,
    ErrorText,
    FieldInputWrapper,
    FieldLabel,
    FieldRow,
    Header,
    Heading,
    LabelInput,
} from "./Styled";

interface DeliveryAddressListProps {
    addresses: CustomerDeliveryAddress[];
    onChange: (addresses: CustomerDeliveryAddress[]) => void;
    validated: boolean;
}

const DeliveryAddressList = ({
    addresses,
    onChange,
    validated,
}: DeliveryAddressListProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canEdit = [ROLE.MANAGER, ROLE.SALE_STAFF].includes(role);
    const defaultGroupName = useId();
    const isFull = addresses.length >= MAX_CUSTOMER_DELIVERY_ADDRESSES;
    const errors = useMemo(
        () => validated ? getDeliveryAddressFieldErrors(addresses) : {},
        [addresses, validated],
    );
    const defaultError = validated ? getDefaultDeliveryAddressValidationMessage(addresses) : null;

    const updateAddress = (
        index: number,
        field: "label" | "address",
        value: string,
    ) => {
        onChange(
            addresses.map((item, itemIndex) =>
                itemIndex === index ? { ...item, [field]: value } : item,
            ),
        );
    };

    const removeAddress = (index: number) => {
        onChange(removeDeliveryAddress(addresses, index));
    };

    const addAddress = () => {
        if (isFull) return;
        onChange([...addresses, { label: "", address: "", is_default: addresses.length === 0 }]);
    };

    return (
        <AddressSection aria-labelledby="delivery-address-heading">
            <Header>
                <Heading id="delivery-address-heading">Địa chỉ giao hàng</Heading>
                <Counter aria-label={`${addresses.length} trên 5 địa chỉ`}>
                    {addresses.length}/{MAX_CUSTOMER_DELIVERY_ADDRESSES}
                </Counter>
            </Header>

            {addresses.length === 0 ? (
                <EmptyState>Chưa có địa chỉ giao hàng</EmptyState>
            ) : (
                <AddressList>
                    {addresses.map((item, index) => (
                        <AddressGroup key={item.id || `new-address-${index}`}>
                            {(canEdit || item.is_default === true) && (
                                <DefaultRow>
                                    {canEdit ? (
                                        <DefaultControl>
                                            <input
                                                type="radio"
                                                name={defaultGroupName}
                                                aria-label={`Mặc định địa chỉ ${index + 1}`}
                                                checked={item.is_default === true}
                                                onChange={() => onChange(selectDefaultDeliveryAddress(addresses, index))}
                                            />
                                            <span>Mặc định</span>
                                        </DefaultControl>
                                    ) : <DefaultBadge>Mặc định</DefaultBadge>}
                                </DefaultRow>
                            )}
                            <FieldRow>
                                <FieldLabel>Tên gợi nhớ {index + 1}</FieldLabel>
                                <FieldInputWrapper>
                                    <LabelInput
                                        aria-label={`Tên gợi nhớ địa chỉ ${index + 1}`}
                                        value={item.label}
                                        placeholder="Vui lòng nhập"
                                        disabled={!canEdit}
                                        $invalid={Boolean(errors[index]?.label)}
                                        onChange={(event) =>
                                            updateAddress(index, "label", event.target.value)
                                        }
                                    />
                                    {errors[index]?.label && (
                                        <ErrorText>{errors[index].label}</ErrorText>
                                    )}
                                </FieldInputWrapper>
                                {canEdit && (
                                    <ActionSlot>
                                        <Tooltip title="Xóa địa chỉ" arrow>
                                            <DeleteButton
                                                type="button"
                                                aria-label={`Xóa địa chỉ ${index + 1}`}
                                                onClick={() => removeAddress(index)}
                                            >
                                                <DeleteOutlineRoundedIcon />
                                            </DeleteButton>
                                        </Tooltip>
                                    </ActionSlot>
                                )}
                            </FieldRow>
                            <FieldRow>
                                <FieldLabel>Địa chỉ đầy đủ</FieldLabel>
                                <FieldInputWrapper>
                                    <AddressInput
                                        aria-label={`Địa chỉ đầy đủ ${index + 1}`}
                                        value={item.address}
                                        placeholder="Vui lòng nhập"
                                        disabled={!canEdit}
                                        $invalid={Boolean(errors[index]?.address)}
                                        onChange={(event) =>
                                            updateAddress(index, "address", event.target.value)
                                        }
                                    />
                                    {errors[index]?.address && (
                                        <ErrorText>{errors[index].address}</ErrorText>
                                    )}
                                </FieldInputWrapper>
                                {canEdit && <ActionSlot aria-hidden="true" />}
                            </FieldRow>
                        </AddressGroup>
                    ))}
                </AddressList>
            )}

            {defaultError && <ErrorText>{defaultError}</ErrorText>}

            {canEdit && (
                <AddButton
                    type="button"
                    disabled={isFull}
                    onClick={addAddress}
                >
                    <AddButtonText>
                        {isFull ? "Đã đạt tối đa 5 địa chỉ" : "Thêm địa chỉ giao hàng"}
                    </AddButtonText>
                    <AddSectionIcon />
                </AddButton>
            )}
        </AddressSection>
    );
};

export default DeliveryAddressList;
